import type { CreSignal, SignalType, SignalStatus } from '../../types';

export const SIGNAL_TYPE_META: Record<SignalType, { label: string; emoji: string; color: string }> = {
  expansion: { label: 'Expansion', emoji: '🟢', color: 'bg-success-wash text-success' },
  distress: { label: 'Distress', emoji: '🔴', color: 'bg-red-100 text-red-800' },
  construction: { label: 'Construction', emoji: '🏗️', color: 'bg-warning-wash text-warning' },
  debt: { label: 'Debt & Ownership', emoji: '💰', color: 'bg-gray-100 text-gray-800' },
  market: { label: 'Market Intel', emoji: '📊', color: 'bg-info-wash text-info' },
};

export function confidenceColor(score: number): string {
  if (score >= 5) return 'bg-red-100 text-red-700';
  if (score >= 4) return 'bg-warning-wash text-warning';
  if (score >= 3) return 'bg-warning-wash text-warning';
  if (score >= 2) return 'bg-info-wash text-info';
  return 'bg-gray-100 text-gray-600';
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

interface SignalCardProps {
  signal: CreSignal;
  onStatus: (id: string, status: SignalStatus) => void;
  highlight?: boolean;
}

export default function SignalCard({ signal, onStatus, highlight = false }: SignalCardProps) {
  const meta = SIGNAL_TYPE_META[signal.signal_type] || SIGNAL_TYPE_META.market;

  return (
    <div className={`bg-white rounded-xl border p-4 ${highlight ? 'border-warning/30 ring-1 ring-warning/30' : ''}`}>
      {/* Badges */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
          {meta.emoji} {meta.label}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${confidenceColor(signal.confidence)}`}>
          {signal.confidence}/5
        </span>
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          {signal.submarket}
        </span>
        {signal.status === 'reviewed' && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success-wash text-success">Reviewed</span>
        )}
        {signal.pushed_to_ghl && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-info-wash text-info">In GHL</span>
        )}
      </div>

      {/* Title */}
      <h3 className="text-sm font-semibold text-gray-900">{signal.company_or_property}</h3>

      {/* Summary */}
      {signal.summary && <p className="text-sm text-gray-600 mt-1">{signal.summary}</p>}

      {/* Detail grid */}
      <div className="mt-3 space-y-1.5 text-sm">
        {signal.opportunity && (
          <p className="text-gray-700">
            <span className="font-medium text-gray-500">Opportunity: </span>
            {signal.opportunity}
          </p>
        )}
        {signal.recommended_action && (
          <p className="text-gray-700">
            <span className="font-medium text-gray-500">Action: </span>
            {signal.recommended_action}
          </p>
        )}
        {signal.decision_maker && (
          <p className="text-gray-700">
            <span className="font-medium text-gray-500">Decision maker: </span>
            {signal.decision_maker}
          </p>
        )}
      </div>

      {/* Sources */}
      {signal.source_urls && signal.source_urls.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {signal.source_urls.map((url, i) =>
            /^https?:\/\//i.test(url) ? (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 py-0.5 rounded-md text-xs bg-gray-50 border text-primary-500 hover:bg-primary-50"
              >
                {hostOf(url)} ↗
              </a>
            ) : (
              <span key={i} className="px-2 py-0.5 rounded-md text-xs bg-gray-50 border text-gray-500">
                {url}
              </span>
            )
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
        {signal.status !== 'reviewed' && (
          <button
            onClick={() => onStatus(signal.id, 'reviewed')}
            className="px-3 py-1.5 text-xs font-medium text-success bg-success-wash rounded-lg hover:bg-success-wash transition-colors"
          >
            Mark Reviewed
          </button>
        )}
        <button
          onClick={() => onStatus(signal.id, 'dismissed')}
          className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
